// repo/primary-care-connect/frontend/src/pages/Portal/CommunityNavigator/Dashboard/DashboardPage.tsx
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { portalFetch } from "../../../../utils/portalRoleChoice";
import "./DashboardPage.css";

type WorkQueues = {
  myActiveCases: number;
  unassigned: number;
  intakeNeeded: number;
  matchesReady: number;
  waitingForClinic: number;
  needsFollowUpRematch: number;
  phoneFollowUpRequired: number;
  casesToClose: number;
};

type DashboardSummaryResponse = {
  ok: boolean;
  mode?: string;
  user?: {
    userId: number;
    role: string;
    ohtId: number;
    ohtName: string | null;
    ohtCode: string | null;
  };
  workQueues?: WorkQueues;
  error?: string;
};

type CommandCard = {
  label: string;
  value: string;
  helper: string;
  actionLabel: string;
  to: string;
};

function formatCount(value: number | undefined | null): string {
  if (typeof value !== "number" || Number.isNaN(value)) return "0";
  return value.toLocaleString();
}

export default function DashboardPage() {
  const [summary, setSummary] = useState<DashboardSummaryResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const isMountedRef = useRef(true);

  const loadSummary = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);

    try {
      const response = await portalFetch(
        "/api/portal/community-navigator/cases?summary=1",
        {
          method: "GET",
          credentials: "include",
        }
      );

      const data = (await response.json()) as DashboardSummaryResponse;

      if (!response.ok || !data.ok) {
        throw new Error(
          data?.error || "Unable to load navigator dashboard summary."
        );
      }

      if (isMountedRef.current) {
        setSummary(data);
      }
    } catch (err) {
      if (isMountedRef.current) {
        setLoadError(
          err instanceof Error
            ? err.message
            : "Unable to load navigator dashboard summary."
        );
      }
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    void loadSummary();

    return () => {
      isMountedRef.current = false;
    };
  }, [loadSummary]);

  const hasSummary = Boolean(summary?.workQueues);
  const workQueues = summary?.workQueues;

  const commandCards: CommandCard[] = useMemo(
    () => [
      {
        label: "My active cases",
        value: hasSummary ? formatCount(workQueues?.myActiveCases) : "—",
        helper: "Cases assigned to you that are still open",
        actionLabel: "View cases",
        to: "/portal/community-navigator/cases?scope=mine&active=1",
      },
      {
        label: "Unassigned",
        value: hasSummary ? formatCount(workQueues?.unassigned) : "—",
        helper: "Open cases waiting to be claimed",
        actionLabel: "View cases",
        to: "/portal/community-navigator/cases?scope=unassigned&active=1",
      },
      {
        label: "Intake needed",
        value: hasSummary ? formatCount(workQueues?.intakeNeeded) : "—",
        helper: "Your cases that need intake completed",
        actionLabel: "View cases",
        to: "/portal/community-navigator/cases?scope=mine&active=1&nextAction=CompleteIntake",
      },
      {
        label: "Select provider",
        value: hasSummary ? formatCount(workQueues?.matchesReady) : "—",
        helper: "Your cases with a match ready to review",
        actionLabel: "View cases",
        to: "/portal/community-navigator/cases?scope=mine&active=1&nextAction=SelectProvider",
      },
      {
        label: "Waiting for clinic",
        value: hasSummary ? formatCount(workQueues?.waitingForClinic) : "—",
        helper: "Your cases awaiting a clinic response",
        actionLabel: "View cases",
        to: "/portal/community-navigator/cases?scope=mine&active=1&nextAction=WaitingForClinic",
      },
      {
        label: "Needs follow-up / rematch",
        value: hasSummary
          ? formatCount(workQueues?.needsFollowUpRematch)
          : "—",
        helper: "Your cases needing follow-up or another match",
        actionLabel: "View cases",
        to: "/portal/community-navigator/cases?scope=mine&active=1&nextActionGroup=followUpRematch",
      },
      {
        label: "Phone follow-up required",
        value: hasSummary
          ? formatCount(workQueues?.phoneFollowUpRequired)
          : "—",
        helper: "Patients without email who need a phone update",
        actionLabel: "View cases",
        to: "/portal/community-navigator/cases?scope=mine&active=1&phoneFollowUpRequired=1",
      },
      {
        label: "Cases to close",
        value: hasSummary ? formatCount(workQueues?.casesToClose) : "—",
        helper: "Your open cases waiting for final closure",
        actionLabel: "View cases",
        to: "/portal/community-navigator/cases?scope=mine&active=1&nextAction=CloseCase",
      },
    ],
    [hasSummary, workQueues]
  );

  return (
    <div className="cn-dashboard-page">
      <section className="cn-dashboard-hero">
        <div>
          <h1>Navigator dashboard</h1>
          <p className="cn-dashboard-subtitle">
            What requires my attention? Start from the work queues below.
          </p>

          {summary?.user ? (
            <p className="cn-dashboard-scope">
              Scope: <strong>{summary.user.ohtName || "Assigned OHT"}</strong>
              {summary.user.ohtCode ? (
                <span> · {summary.user.ohtCode}</span>
              ) : null}
            </p>
          ) : null}
        </div>

        <div className="cn-dashboard-hero-actions">
          <Link
            to="/portal/community-navigator/uploads"
            className="cn-dashboard-secondary-action"
          >
            Upload list
          </Link>
        </div>
      </section>

      {loadError ? (
        <section className="cn-dashboard-alert" role="alert">
          <h2>Dashboard could not load</h2>
          <p>{loadError}</p>
          <button
            type="button"
            className="cn-dashboard-retry"
            onClick={() => {
              void loadSummary();
            }}
            disabled={isLoading}
          >
            Retry
          </button>
        </section>
      ) : null}

      <section className="cn-dashboard-section">
        <div className="cn-dashboard-section-header">
          <div>
            <h2>Work queues</h2>
            <p>
              Each card opens the exact matching Cases queue for that work.
            </p>
          </div>
        </div>

        <div className="cn-dashboard-command-grid">
          {commandCards.map((card) => (
            <Link
              key={card.label}
              to={card.to}
              className="cn-dashboard-command-card-link"
            >
              <article className="cn-dashboard-command-card">
                <div>
                  <span>{card.label}</span>
                  <strong>{card.value}</strong>
                  <p>{card.helper}</p>
                </div>
                <span className="cn-dashboard-command-card-action">
                  {card.actionLabel}
                </span>
              </article>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
